import Button from '@admin/components/button'
import Format from '@admin/components/format'
import Icon from '@admin/components/icon'
import Token from '@admin/tokens/token'
import T from '@admin/components/t'
import PropTypes from 'prop-types'
import pluralize from 'pluralize'
import React, { Fragment } from 'react'

class Select extends React.Component {

  static propTypes = {
    columns: PropTypes.number,
    compareKey: PropTypes.string,
    defaultSelected: PropTypes.bool,
    defaultValue: PropTypes.any,
    deselectable: PropTypes.bool,
    disabled: PropTypes.any,
    entity: PropTypes.string,
    format: PropTypes.any,
    groupKey: PropTypes.string,
    groupFormat: PropTypes.any,
    height: PropTypes.number,
    items: PropTypes.array,
    id: PropTypes.string,
    label: PropTypes.string,
    multiple: PropTypes.bool,
    options: PropTypes.array,
    selected: PropTypes.array,
    selectFirst: PropTypes.bool,
    status: PropTypes.string,
    tabIndex: PropTypes.number,
    textKey: PropTypes.string,
    valueKey: PropTypes.string,
    value: PropTypes.any,
    onBusy: PropTypes.func,
    onChange: PropTypes.func,
    onReady: PropTypes.func
  }

  static defaultProps = {
    columns: 1,
    deselectable: false,
    entity: 'option',
    format: Token,
    multiple: false,
    options: [],
    selectFirst: false,
    textKey: 'text',
    valueKey: 'value',
    onBusy: () => {},
    onChange: () => {},
    onReady: () => {}
  }

  state = {
    selected: null,
    focused: 0,
    isFocused: false
  }

  _handleBlur = this._handleBlur.bind(this) 
  _handleDeselectAll = this._handleDeselectAll.bind(this)
  _handleFocus = this._handleFocus.bind(this) 
  _handleKeyDown = this._handleKeyDown.bind(this)
  _handleSelectAll = this._handleSelectAll.bind(this) 

  render() {
    const { entity, format, groupFormat, options, tabIndex } = this.props
    if(!this.state.selected) return null
    const deselectable = this._getDeselectable()
    const grouped = this._getGrouped()
    return (
      <div className={ this._getClass() } tabIndex={ tabIndex } onKeyDown={ this._handleKeyDown } onFocus={ this._handleFocus } onBlur={ this._handleBlur }>
        { deselectable && options.length > 1 &&
          <div className="maha-select-deselect">
            <Button { ...this._getDeselect() } />
          </div>
        }
        <div className="maha-select-options" style={ this._getStyle() }>
          { options.length === 0 &&
            <div className="maha-select-option-empty">
              There are no { pluralize(entity) }
            </div>
          }
          { Object.keys(grouped).map((group, i) => (
            <Fragment key={`group_${i}`}>
              { group !== 'default' &&
                <div className={`maha-search-results-header${ groupFormat ? ' compact' : '' }`}>
                  { groupFormat ? 
                    <Format format={ groupFormat } value={ group } /> :
                    <T text={ group } />
                  }
                </div>
              }
              { grouped[group].map(({ option, index }, j) => (
                <div key={`option_${j}`} { ...this._getOption(index) }>
                  <div className="maha-select-option-icon">
                    <Icon icon={`${this._getOptionIcon(index)}`} />
                  </div>
                  <div className="maha-select-option-label">
                    <Format { ...option } format={ format } value={ this._getText(option) } />
                  </div>
                  { option.extra &&
                    <div className="maha-select-option-extra">
                      { option.extra }
                    </div>
                  }
                </div>
              )) }
            </Fragment>
          )) }
        </div>
      </div>
    )
  }

  componentDidMount() {
    const defaultValue = this._getDefaultValue()
    if(!_.isNil(defaultValue)) this._handleSet(defaultValue, true)
    this.props.onReady()
  }

  componentDidUpdate(prevProps, prevState) {
    const { selectFirst, value, options } = this.props
    const { selected, focused } = this.state
    const newIndexes = this._getIndexes(value)
    if(!_.isEqual(value, prevProps.value) && !_.isEqual(newIndexes, selected)) {
      if (newIndexes.includes(focused)) {
        this._handleSet(value, false)
      } else {
        this._handleSet(value, true)
      }
    }
    if(!_.isEqual(selected, prevState.selected)) {
      if(prevState.selected === null && !selectFirst) return
      this._handleChange()
    }
    if (options.length && (focused == null || focused > options.length - 1)) {
      this.setState({ focused: 0 })
    }
  }

  _getClass() {
    const numbers = ['one','two','three','four','five','six']
    const { label, columns, disabled } = this.props
    const deselectable = this._getDeselectable()
    const classes = ['maha-input','maha-select',`${numbers[columns-1]}-column`]
    if(disabled) classes.push('disabled')
    if(deselectable && !label) classes.push('unlabeled')
    return classes.join(' ')
  }

  _getDefaultValue() {
    const { defaultValue, value, selectFirst, compareKey, valueKey } = this.props
    const { options } = this.props
    const key = valueKey || compareKey
    const first = options.length > 0 ? _.get(options[0], key) : []
    return !_.isNil(value) ? value : !_.isNil(defaultValue) ? defaultValue : (selectFirst ? first : [])
  }
  
  _getDeselect() {
    const { multiple } = this.props
    const { selected } = this.state
    return {
      label: multiple ? (selected.length > 0 ? 'deselect all' : 'select all') : 'deselect',
      className: (!multiple && selected.length === 0) ? 'link disabled' : 'link',
      handler: (multiple && selected.length === 0) ? this._handleSelectAll : this._handleDeselectAll
    }
  }
  
  _getDeselectable() { 
    const { deselectable, multiple } = this.props
    return deselectable === false ? false : multiple
  }

  _getGrouped() {
    const { groupKey, options } = this.props
    return options.reduce((grouped, option, index) => ({
      ...grouped,
      ...groupKey && option[groupKey] ? {
        [option[groupKey]]: [
          ...grouped[option[groupKey]] || [],
          { option, index }
        ]
      } : {
        default: [
          ...grouped.default || [],
          { option, index }
        ]
      }
    }), {})
  }

  _getIndexes(value) {
    if(value === null) return []
    const { options, valueKey } = this.props
    return options.reduce((indexes, option, index) => {
      const isEqual = _.castArray(value).find(val => {
        const optionValue = valueKey ? _.get(option, valueKey) : option
        return _.isEqual(optionValue, val)
      }) !== undefined
      return [
        ...indexes,
        ...isEqual ? [index] : []
      ]
    }, [])
  }

  _getIsSelected(index) {
    const { selected } = this.state
    return _.includes(selected, index)
  }

  _getOption(index) {
    const isFocused = this.state.isFocused && this.state.focused === index
    return {
      className: this._getOptionClass(index) + (isFocused ? ' focused' : ''),
      onClick: () => this._handleUpdate([index], index),
      onMouseEnter: () => this.setState({ focused: index })
    }
  }

  _getOptionClass(index) {
    const classes = ['maha-select-option']
    if(this._getIsSelected(index)) classes.push('selected')
    return classes.join(' ')
  }

  _getOptionIcon(index) {
    const { multiple } = this.props
    const selected = this._getIsSelected(index)
    if(selected) return multiple ? 'check-square' : 'check-circle'
    if(!selected) return multiple ? 'square-o' : 'circle-o'
  }

  _getStyle() {
    const { height } = this.props
    return height ?{
      overflowY: 'scroll',
      maxHeight: height
    } : {}
  }

  _getText(option) {
    const { textKey } = this.props
    return textKey ? _.get(option, textKey) : option
  }

  _getValue(index) {
    if(index === null) return null
    const { options, compareKey, valueKey } = this.props
    const key = valueKey || compareKey
    const option = options[index]
    return valueKey ? _.get(option, key) : option
  }

  _handleBlur() {
    this.setState({ isFocused: false })
  }
  
  _handleChange() {
    const { multiple } = this.props
    const { selected } = this.state
    const values = selected.map(index => {
      return this._getValue(index)
    })
    const value = multiple ? values : values[0]
    this.props.onChange(_.isNil(value) ? null : value)
  }

  _handleDeselectAll() {
    this._handleSet([], true)
  }

  _handleFocus() {
    this.setState({ isFocused: true })
  }

  _handleKeyDown(e) {
    const { options } = this.props
    const { focused } = this.state
    const max = options.length - 1
    if ([38, 40].includes(e.which)) {
      e.preventDefault()
      let next = focused
      if (e.which === 38) next = (focused <= 0) ? max : focused - 1
      if (e.which === 40) next = (focused >= max) ? 0 : focused + 1
      this.setState({ focused: next })
      return
    }
    if ([13, 32].includes(e.which)) {
      e.preventDefault()
      this._handleUpdate([focused])
      return
    }
  }

  _handleSelectAll() {
    const { options } = this.props
    this._handleSet(options.map((option, index) => index), true)
  }

  _handleSet(value, updateFocus = true) {
    const selected = this._getIndexes(value)
    if (updateFocus && !_.isEqual(selected, this.state.selected)) {
      this.setState({ selected, focused: selected.length ? selected[0] : 0 })
    } else if (!_.isEqual(selected, this.state.selected)) {
      this.setState({ selected })
    }
  }

  _handleUpdate(indexes, focusIndex) {
    const { disabled, multiple } = this.props
    const { selected } = this.state
    if(disabled) return
    if (typeof focusIndex === 'number') {
      this.setState({
        selected: multiple ? _.xor(selected, indexes) : indexes,
        focused: focusIndex
      })
    } else {
      this.setState({
        selected: multiple ? _.xor(selected, indexes) : indexes
      })
    }
  }

}

export default Select
